/**
 * 2026-10-30 (owner, filling design) · where a step's value comes from — Captured from a machine tag,
 * Entered by a person, or Calculated — and, when captured, the tag (machine › tag), when it is read,
 * how old it may be and what happens when there is none. Saved on the step's monitoring row with the
 * rest of the monitoring (services/recipe/capture.py); the words and the plant's defaults are served.
 */
import React, { useEffect, useMemo, useState } from "react";
import { InputNumber, Segmented, Select, Tooltip } from "antd";
import { Info } from "lucide-react";
import { useParams } from "react-router-dom";
import { getCaptureOptions } from "../../../pages/Batch-Processing/mbmr/designerApi";
import { Field } from "./Field";

/** The record plant's tags, words and defaults; null until loaded, or where there is no record. */
export function useCaptureOptions() {
  const { recipe_id: recipeId } = useParams();
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    let live = true;
    if (recipeId) getCaptureOptions(recipeId).then((o) => { if (live) setOpts(o); });
    return () => { live = false; };
  }, [recipeId]);
  return opts;
}

function Tip({ text }) {
  return (
    <Tooltip title={text} placement="topRight">
      <span className="u0-info" role="img" aria-label="About value source" tabIndex={0}>
        <Info size={14} strokeWidth={1.9} />
      </span>
    </Tooltip>
  );
}

/** Tag options grouped by machine: "FM_101.fill_g_h1 · g · 3.41". */
export function tagOptions(tags = []) {
  const groups = {};
  tags.forEach((t) => {
    const g = t.machine_code ? `${t.machine} (${t.machine_code})` : t.machine || "—";
    (groups[g] = groups[g] || []).push({
      value: t.id,
      label: [t.unique_id || t.name, t.unit, t.current_value != null && t.current_value !== "" ? `now ${t.current_value}` : null]
        .filter(Boolean).join(" · "),
      search: `${t.machine} ${t.machine_code} ${t.unique_id} ${t.name}`.toLowerCase(),
    });
  });
  return Object.entries(groups).map(([label, options]) => ({ label, options }));
}

export function TagPicker({ value, onChange, readOnly, tags, status }) {
  const options = useMemo(() => tagOptions(tags), [tags]);
  return (
    <Select size="small" showSearch allowClear value={value ?? undefined} disabled={readOnly} status={status}
            placeholder="machine › tag" style={{ width: "100%" }} options={options}
            filterOption={(input, o) => (o?.search || "").includes(input.toLowerCase())}
            onChange={(v) => onChange(v ?? null)} />
  );
}

export function CaptureEditor({ m, readOnly, patch, showErrors = false }) {
  const opts = useCaptureOptions();
  const source = m.source || "ENTERED";
  const sources = opts?.sources || [];
  const tag = (opts?.tags || []).find((t) => t.id === m.tag_id) || (m.tag?.id === m.tag_id ? m.tag : null);
  const words = (k) => opts?.[k] || [];
  return (
    <div className="u0-mon-grid" data-testid="capture">
      <Field label={<>Source <Tip text="Captured is read once from the machine tag at the moment chosen; the operator sees it and cannot overwrite it. Entered is typed and signed. Calculated is worked from other values." /></>}>
        <Segmented size="small" value={source} disabled={readOnly || !sources.length}
                   options={sources.map((s) => ({ value: s.value, label: s.label }))}
                   onChange={(v) => patch({ source: v })} />
      </Field>
      {source === "CAPTURED" ? (
        <>
          <Field label="Tag" required={!readOnly}>
            <TagPicker value={m.tag_id} readOnly={readOnly} tags={opts?.tags || []}
                       status={showErrors && !m.tag_id ? "error" : undefined}
                       onChange={(v) => patch({ tag_id: v })} />
            {tag ? (
              <span className="u0-help" style={{ display: "block" }}>
                {[tag.machine, tag.unit, tag.current_value != null && tag.current_value !== "" ? `now ${tag.current_value}` : null]
                  .filter(Boolean).join(" · ")}
              </span>
            ) : null}
          </Field>
          <Field label="Read at" required={!readOnly}>
            <Select size="small" value={m.read_at || undefined} disabled={readOnly} placeholder="— choose —"
                    status={showErrors && !m.read_at ? "error" : undefined}
                    options={words("read_at")} onChange={(v) => patch({ read_at: v })} />
          </Field>
          <Field label="Max age">
            <InputNumber size="small" min={1} value={m.max_age_seconds ?? null} disabled={readOnly} suffix="s"
                         placeholder={opts?.defaults?.max_age_seconds != null ? String(opts.defaults.max_age_seconds) : undefined}
                         onChange={(v) => patch({ max_age_seconds: v, max_age_from_plant: false })} />
            {m.max_age_from_plant ? <span className="u0-help" style={{ display: "block" }}>Plant default</span> : null}
          </Field>
          <Field label="If no value">
            <Select size="small" value={m.if_no_value || "MANUAL_WITH_REASON"} disabled={readOnly}
                    options={words("if_no_value")} onChange={(v) => patch({ if_no_value: v })} />
          </Field>
        </>
      ) : null}
    </div>
  );
}

/** "DCS" on a step card whose value is captured from a machine tag. */
export function DcsChip({ monitoring }) {
  if (!monitoring || monitoring.source !== "CAPTURED") return null;
  const t = monitoring.tag?.unique_id;
  return (
    <span className="dz-chip dz-chipi dz-dcstag" title={t ? `Captured from ${t}` : "Captured — no tag chosen"}
          data-testid="dcs-chip">DCS</span>
  );
}

/** A check row's settings in LoopForm: trigger, counter tag, every, heads, due window. */
export function CheckRowFields({ cfg = {}, readOnly, onChange, showErrors = false }) {
  const opts = useCaptureOptions();
  const set = (delta) => onChange({ ...cfg, ...delta });
  const trigger = cfg.trigger || "";
  const heads = opts?.defaults?.heads_max;
  return (
    <div className="u0-grid2" data-testid="check-row">
      <Field label="Trigger" required={!readOnly}>
        <Select size="small" value={trigger || undefined} disabled={readOnly} placeholder="— choose —"
                status={showErrors && !trigger ? "error" : undefined} options={opts?.triggers || []}
                onChange={(v) => set({ trigger: v, ...(v === "COUNTER_TAG" ? {} : { tag_id: null }),
                                       ...(v === "STEP_SIGN_OFF" ? { every: null } : {}) })} />
      </Field>
      {trigger === "COUNTER_TAG" ? (
        <Field label="Counter tag" required={!readOnly}>
          <TagPicker value={cfg.tag_id} readOnly={readOnly} tags={opts?.tags || []}
                     status={showErrors && !cfg.tag_id ? "error" : undefined}
                     onChange={(v) => set({ tag_id: v })} />
        </Field>
      ) : null}
      {trigger === "COUNTER_TAG" || trigger === "INTERVAL_MINUTES" ? (
        <Field label="Every" required={!readOnly && trigger === "INTERVAL_MINUTES"}>
          <InputNumber size="small" min={1} value={cfg.every ?? null} disabled={readOnly}
                       suffix={trigger === "COUNTER_TAG" ? "units" : "min"} style={{ width: "100%" }}
                       onChange={(v) => set({ every: v })} />
        </Field>
      ) : null}
      <Field label="Heads">
        <InputNumber size="small" min={1} max={heads ?? undefined} value={cfg.heads ?? null} disabled={readOnly}
                     style={{ width: "100%" }} onChange={(v) => set({ heads: v })} />
      </Field>
      <Field label="Due window">
        <InputNumber size="small" min={1} value={cfg.due_minutes ?? null} disabled={readOnly} suffix="min"
                     placeholder={opts?.defaults?.due_minutes != null ? String(opts.defaults.due_minutes) : undefined}
                     style={{ width: "100%" }} onChange={(v) => set({ due_minutes: v, due_from_plant: false })} />
        {cfg.due_from_plant ? <span className="u0-help" style={{ display: "block" }}>Plant default</span> : null}
      </Field>
    </div>
  );
}
